import React from 'react';
import { ResponsivePie } from '@nivo/pie';

const defaultCenterTextStyle = {
  fontSize: '18px',
  fontWeight: '400',
  fontFamily: 'var(--font-main)',
  fill: '#333333',
  textAnchor: 'middle',
  dominantBaseline: 'middle',
};

// Function to wrap text
const wrapText = (text, maxWidth, fontSize) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = getTextWidth(currentLine + " " + word, `${fontSize} ${defaultCenterTextStyle.fontFamily}`);
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Helper function to get text width
const getTextWidth = (text, font) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

/**
 * NivoDonut - A customizable donut chart component using Nivo with text wrapping and digital gradient.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.data - An array of objects representing the chart data.
 *   Each object should have the following structure:
 *   { id: string, value: number, color: string }
 * @param {string} [props.centerText] - Optional text to display in the center of the donut.
 * @param {Object} [props.centerTextStyle] - Optional style object for the center text.
 *
 * @example
 * <NivoDonut
 *   data={[
 *     { id: 'A', value: 30, color: '#3182bd' },
 *     { id: 'B', value: 70, color: '#6baed6' }
 *   ]}
 *   centerText="Asset Classes"
 *   centerTextStyle={{ fontSize: '16px', fill: '#555' }}
 * />
 *
 * @returns {React.Component} A responsive donut chart component with wrapped text and digital gradient.
 */
const NivoDonut = ({ data, centerText, centerTextStyle }) => {
  const mergedCenterTextStyle = { ...defaultCenterTextStyle, ...centerTextStyle };


  return (
    <ResponsivePie
      data={data}
      margin={{ top: 20, right: 160, bottom: 20, left: 20 }}
      innerRadius={0.7}
      padAngle={0.7}
      cornerRadius={3}
      activeOuterRadiusOffset={8}
      borderWidth={1}
      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
      arcLinkLabelsSkipAngle={10}
      arcLinkLabelsTextColor="#333333"
      enableArcLinkLabels={false}
      arcLinkLabelsThickness={2}
      arcLinkLabelsColor={{ from: 'color' }}
      arcLabelsSkipAngle={10}
      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
      colors={({ data }) => data.color}
      defs={data.map(item => ({
        id: `gradient-${item.id}`,
        type: 'linearGradient',
        colors: [
          { offset: 0, color: item.color, opacity: 1 },
          { offset: 50, color: item.color, opacity: 0.7 },
          { offset: 100, color: item.color, opacity: 0.5 },
        ],
        gradientTransform: 'rotate(90 0.5 0.5)', // This creates a more vertical gradient
      }))}
      fill={data.map(item => ({ match: { id: item.id }, id: `gradient-${item.id}` }))}
      theme={{
        labels: {
          text: { fontSize: 14, fontWeight: 'bold' },
        },
        legends: {
          text: { fontSize: 12 },
        },
      }}
      legends={[
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 0,
          translateY: 10,
          itemsSpacing: 10,
          itemWidth: 140,
          itemHeight: 30,
          itemTextColor: '#999',
          itemDirection: 'left-to-right',
          itemOpacity: 1,
          symbolSize: 18,
          symbolShape: 'circle',
        },
      ]}
      layers={[
        'arcs',
        'arcLabels',
        'arcLinkLabels',
        'legends',
        ({ centerX, centerY, innerRadius }) => {
          if (!centerText) return null;
          const lines = wrapText(centerText, innerRadius * 1.5, mergedCenterTextStyle.fontSize);
          return (
            <g>
              {lines.map((line, i) => (
                <text
                  key={i}
                  x={centerX}
                  y={centerY + (i - (lines.length - 1) / 2) * parseInt(mergedCenterTextStyle.fontSize)}
                  style={mergedCenterTextStyle}
                  textAnchor="middle"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        }
      ]}
    />
  );
};

export default NivoDonut;